import React, { useMemo } from "react";
import { ArrowRightIcon } from "lucide-react";
import { useBookings } from "../../contexts/BookingsContext";
import { getBookableDays, getSlotsForDay, toDateKey } from "../../utils/slots";
import { formatLongDate, formatMonthShort, formatTime, formatWeekdayShort } from "../../utils/format";
import type { Service } from "../../types/booking";
interface SlotStepProps {
  service: Service;
  dateKey: string | null;
  time: string | null;
  onSelectDate: (key: string) => void;
  onSelectTime: (time: string) => void;
  onContinue: () => void;
}
const periods = [{
  label: "Matin",
  test: (t: string) => t < "12:30"
}, {
  label: "Après-midi",
  test: (t: string) => t >= "12:30" && t < "17:00"
}, {
  label: "Fin de journée",
  test: (t: string) => t >= "17:00"
}];
export function SlotStep({
  service,
  dateKey,
  time,
  onSelectDate,
  onSelectTime,
  onContinue
}: SlotStepProps) {
  const {
    isSlotTaken
  } = useBookings();
  const days = useMemo(() => getBookableDays(14).map(date => {
    const available = getSlotsForDay(date, service.duration, isSlotTaken).filter(s => s.available);
    return {
      date,
      key: toDateKey(date),
      available
    };
  }), [service.duration, isSlotTaken]);
  const selectedDay = days.find(d => d.key === dateKey) ?? null;
  return <div data-id="element-140" data-mp="13ol9sgcagje4">
      <h2 className="text-xl font-semibold text-ink" data-id="element-141" data-mp="13na8qoa89pqp">Choisissez votre créneau</h2>
      <p className="mt-1 text-sm text-muted" data-id="element-142" data-mp="61csyyn8fyig">
        {service.name} · {service.duration} min
      </p>

      <div className="no-scrollbar -mx-6 mt-6 overflow-x-auto px-6 md:-mx-8 md:px-8" data-id="element-143" data-mp="ayuva66qvfhh">
        <div role="radiogroup" aria-label="Date du rendez-vous" className="flex gap-2 pb-1" data-id="element-144" data-mp="fy7pjbzy3ou3">
          {days.map(day => {
          const selected = day.key === dateKey;
          const full = day.available.length === 0;
          return <button key={day.key} type="button" role="radio" aria-checked={selected} aria-label={`${formatLongDate(day.date)}${full ? ", complet" : ""}`} disabled={full} onClick={() => onSelectDate(day.key)} className={`flex w-16 shrink-0 flex-col items-center rounded-2xl border py-3 transition-colors duration-150 ${selected ? "border-brand bg-brand text-white" : full ? "cursor-not-allowed border-line bg-surface text-muted/60" : "border-line text-ink hover:border-brand/40"}`} data-id="element-145" data-mp="1rdmj05sy3ykg">
                <span className={`text-xs ${selected ? "text-white/80" : "text-muted"}`} data-id="element-146" data-mp="3iihdp6eon66r">
                  {formatWeekdayShort(day.date)}
                </span>
                <span className="mt-0.5 text-lg font-semibold" data-id="element-147" data-mp="3dpek4y9wnwqk">{day.date.getDate()}</span>
                <span className={`text-xs ${selected ? "text-white/80" : "text-muted"}`} data-id="element-148" data-mp="2pzk9mnd26k9h">
                  {full ? "Complet" : formatMonthShort(day.date)}
                </span>
              </button>;
        })}
        </div>
      </div>

      <div className="mt-8" data-id="element-149" data-mp="2gn39fl5vcqna">
        {!selectedDay ? <p className="rounded-2xl bg-surface p-6 text-sm text-muted" data-id="element-150" data-mp="2goo73jnhcppd">
            Sélectionnez une date pour voir les horaires disponibles.
          </p> : <>
            <h3 className="text-sm font-semibold text-ink" data-id="element-151" data-mp="26xbh6xl8s9lz">{formatLongDate(selectedDay.date)}</h3>
            <div className="mt-4 space-y-6" data-id="element-152" data-mp="2une654tv0zgd">
              {periods.map(period => {
            const slots = selectedDay.available.filter(s => period.test(s.time));
            if (slots.length === 0) return null;
            return <div key={period.label} data-id="element-153" data-mp="2pmqd6dm2m8ji">
                    <p className="text-xs text-muted" data-id="element-154" data-mp="2ppmbw45qt190">{period.label}</p>
                    <div role="radiogroup" aria-label={period.label} className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5" data-id="element-155" data-mp="198i4nmy4xzqv">
                      {slots.map(slot => {
                  const selected = slot.time === time;
                  return <button key={slot.time} type="button" role="radio" aria-checked={selected} onClick={() => onSelectTime(slot.time)} className={`rounded-xl border py-2.5 text-sm font-medium transition-colors duration-150 ${selected ? "border-brand bg-brand text-white" : "border-line text-ink hover:border-brand hover:text-brand"}`} data-id="element-156" data-mp="1po90tok9605c">
                            {formatTime(slot.time)}
                          </button>;
                })}
                    </div>
                  </div>;
          })}
            </div>
          </>}
      </div>

      <div className="mt-8 flex justify-end border-t border-line pt-6" data-id="element-157" data-mp="2yqlsjoxwnorw">
        <button type="button" onClick={onContinue} disabled={!time} className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-line disabled:text-muted" data-id="element-158" data-mp="33nue7zi9iaxw">
          Continuer
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-159" data-mp="3ifctfv3as1g7" />
        </button>
      </div>
    </div>;
}